import React from "react";
import { View, StyleSheet, Pressable, ScrollView } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";

interface ShiftSupervisor {
  id: string;
  user_id: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email_address?: string;
    preferred_name?: string;
  };
}

interface SupervisorContactModalProps {
  supervisors?: ShiftSupervisor[];
  shiftId?: string;
  onRetry: () => void;
  onCancel: () => void;
}

export function SupervisorContactModal({ supervisors, shiftId, onRetry, onCancel }: SupervisorContactModalProps) {
  const { colors } = useTheme();

  const handleSupervisorPress = (userId: string) => {
    if (shiftId) {
      router.push(`/home/${shiftId}/${userId}`);
    }
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: colors.amber?.background }]}>
          <MaterialCommunityIcons name="alert-circle-outline" size={32} color={colors.amber?.text} />
        </View>
        <ThemedText style={[styles.title, { color: colors.text }]}>Contact Supervisor Required</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
          You indicated that you did not receive proper breaks during your shift. Please contact a supervisor to resolve
          this issue before clocking out.
        </ThemedText>
      </View>

      {/* Supervisors List */}
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="account-tie" size={24} color={colors.primary} />
          <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Shift Supervisors</ThemedText>
        </View>

        <ScrollView style={styles.supervisorsList}>
          {!supervisors || supervisors.length === 0 ? (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="account-off" size={32} color={colors.placeholder} />
              <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>
                No supervisors assigned to this shift
              </ThemedText>
            </View>
          ) : (
            supervisors.map((supervisor) => {
              const user = supervisor.user;
              const displayName = user
                ? user.preferred_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email_address
                : "Unknown Supervisor";

              return (
                <Pressable
                  key={supervisor.id}
                  onPress={() => handleSupervisorPress(supervisor.user_id)}
                  style={({ pressed, hovered }) => [
                    styles.supervisorItem,
                    { backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
                    hovered && ({ backgroundColor: colors.card } as any),
                    pressed && { opacity: 0.7 },
                  ]}
                >
                  <View
                    style={[
                      styles.avatar,
                      { backgroundColor: colors.amber?.background, borderColor: colors.amber?.border },
                    ]}
                  >
                    <MaterialCommunityIcons name="account-tie" size={20} color={colors.amber?.text} />
                  </View>
                  <View style={styles.supervisorInfo}>
                    <ThemedText style={styles.supervisorName}>{displayName}</ThemedText>
                    {user?.email_address && user.email_address !== displayName && (
                      <ThemedText style={[styles.supervisorEmail, { color: colors.textSecondary }]}>
                        {user.email_address}
                      </ThemedText>
                    )}
                  </View>
                  <MaterialCommunityIcons name="phone" size={20} color={colors.primary} />
                  <MaterialCommunityIcons name="chevron-right" size={20} color={colors.textSecondary} />
                </Pressable>
              );
            })
          )}
        </ScrollView>
      </View>

      {/* Action Buttons */}
      <View style={styles.buttonRow}>
        <Button
          text="Cancel"
          onPress={onCancel}
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text="Try Again"
          icon="refresh"
          onPress={onRetry}
          style={[styles.retryButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white", fontWeight: "600" }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 20,
  },
  header: {
    alignItems: "center",
    gap: 12,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
    paddingHorizontal: 16,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    gap: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  supervisorsList: {
    maxHeight: 200,
  },
  emptyState: {
    alignItems: "center",
    padding: 24,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
    textAlign: "center",
  },
  supervisorItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
  supervisorInfo: {
    flex: 1,
    gap: 2,
  },
  supervisorName: {
    fontSize: 14,
    fontWeight: "500",
  },
  supervisorEmail: {
    fontSize: 12,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    borderRadius: 8,
    minHeight: 48,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  retryButton: {
    flex: 2,
    borderRadius: 8,
    minHeight: 48,
  },
});
